import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import s from './ColorAdjust.module.css';
import Slider from '../../../../shared/ui/slider/Slider';
import { RgbaColorPicker } from 'react-colorful';
import { selectSelectedImage } from '../../../../store/selectors/imageSelectors';
import { updateSelectedImageSetting } from '../../../../store/slices/imageSlice';
import { useAppDispatch } from '../../../../store/store';
import { CollapsiblePanel } from '../../../../shared/ui/collapse-panel/CollapsiblePanel';

import ColorPicker, { useColorPicker } from 'react-best-gradient-color-picker';
import IconBrightness from '../../../../shared/ui/icons/IconBrightness';
import IconContrast from '../../../../shared/ui/icons/IconContrast';
import IconSaturation from '../../../../shared/ui/icons/IconSaturation';
import IconShadows from '../../../../shared/ui/icons/IconShadows';
import IconVignette from '../../../../shared/ui/icons/IconVignette';
import IconHighlights from '../../../../shared/ui/icons/IconHighlights';
import IconHue from '../../../../shared/ui/icons/IconHue';
import IconGrain from '../../../../shared/ui/icons/IconGrain';
import IconRedChannel from '../../../../shared/ui/icons/IconRedChannel';
import IconGreenChanell from '../../../../shared/ui/icons/IconGreenChanell';
import IconBlueChannel from '../../../../shared/ui/icons/IconBlueChannel';
import IconEyeOpen from '../../../../shared/ui/icons/IconEyeOpen';
import IconEyeClosed from '../../../../shared/ui/icons/IconEyeClosed';

const sliderLabels = {
    brightness: 'Brightness',
    contrast: 'Contrast',
    saturation: 'Saturation',
    shadows: 'Shadows',
    //  vignette: 'Vignette',
    highlights: 'Highlights',
    // vibrance: 'Vibrance',
    // clarity: 'Clarity',
    hue: 'Hue',
    // blacks: 'Blacks',
    // white: 'White',
    //  grain: 'Grain',
    // red: 'Red Channel',
    // green: 'Green Channel',
    // blue: 'Blue Channel',
} as const;

type SettingKey = keyof typeof sliderLabels;
type PanelKey = 'visual' | 'canvasColor' | 'transparency' | 'gradient';

const getSliderConfig = (key: SettingKey) => {
    if (key === 'hue') return { min: 0, max: 360, step: 1 };
    // if (key === 'grain') return { min: 0, max: 100, step: 1 };
    // if (key === 'vignette') return { min: 0, max: 100, step: 1 };
    // if (key === 'red' || key === 'green' || key === 'blue') return { min: 0, max: 2, step: 0.01 };
    return { min: -100, max: 100, step: 1 };
};

const sliderIcons = {
    brightness: IconBrightness,
    contrast: IconContrast,
    saturation: IconSaturation,
    shadows: IconShadows,
    // vignette: IconVignette,
    highlights: IconHighlights,
    hue: IconHue,
    //grain: IconGrain,
    // red: IconRedChannel,
    // green: IconGreenChanell,
    // blue: IconBlueChannel,
};

const AdjustVisualsBlock = () => {
    const dispatch = useAppDispatch();
    const selectedImage = useSelector(selectSelectedImage);

    const [filtersEnabled, setFiltersEnabled] = useState<boolean>(true);
    const [savedFilterValues, setSavedFilterValues] = useState<Partial<Record<SettingKey, number>>>(
        {},
    );

    if (!selectedImage?.image) return <p className={s.text}>No image selected</p>;

    const handleChangeVisualSettings = (key: SettingKey, value: number) => {
        dispatch(
            updateSelectedImageSetting({
                key: 'visual_settings' as any,
                value: { ...selectedImage?.image?.visual_settings, [key]: value },
            }),
        );
    };

    const toggleFilters = () => {
        if (filtersEnabled) {
            const currentValues: Partial<Record<SettingKey, number>> = {};
            Object.keys(sliderLabels).forEach((key) => {
                const settingKey = key as SettingKey;
                const currentValue = selectedImage?.image?.visual_settings?.[settingKey] ?? 0;
                currentValues[settingKey] = currentValue;
                console.log(`Saving ${settingKey}: ${currentValue}`);
            });
            setSavedFilterValues(currentValues);

            const resetValues: any = { ...selectedImage?.image?.visual_settings };
            Object.keys(sliderLabels).forEach((key) => {
                const settingKey = key as SettingKey;
                resetValues[settingKey] = 0;
                console.log(`Resetting ${settingKey} to 0`);
            });

            dispatch(
                updateSelectedImageSetting({
                    key: 'visual_settings' as any,
                    value: resetValues,
                }),
            );

            setFiltersEnabled(false);
        } else {
            const restoreValues: any = { ...selectedImage?.image?.visual_settings };
            Object.entries(savedFilterValues).forEach(([key, value]) => {
                const settingKey = key as SettingKey;
                if (value !== undefined) {
                    restoreValues[settingKey] = value;
                }
            });

            dispatch(
                updateSelectedImageSetting({
                    key: 'visual_settings' as any,
                    value: restoreValues,
                }),
            );

            setFiltersEnabled(true);
        }
    };

    return (
        <div className={s.container}>
            <div className={s.header}>
                <div>
                    <h3 className={s.title}>Adjust Colors</h3>
                    <p className={s.subtitle}>Modify colors, light, and tone of your photo.</p>
                </div>
                <button
                    className={s.eye_button}
                    onClick={toggleFilters}
                    title={filtersEnabled ? 'Disable filters' : 'Enable filters'}
                    style={{
                        backgroundColor: filtersEnabled ? '#fff' : '#FFDD35',
                    }}
                >
                    {filtersEnabled ? (
                        <IconEyeClosed width={20} height={20} color="#999" />
                    ) : (
                        <IconEyeOpen width={20} height={20} color="#333" />
                    )}
                </button>
            </div>
            <div className={`${s.block_container} ${!filtersEnabled ? s.disabled : ''}`}>
                {Object.entries(sliderLabels).map(([key, label]) => {
                    const settingKey = key as SettingKey;
                    const { min, max, step } = getSliderConfig(settingKey);
                    const value = selectedImage?.image?.visual_settings?.[settingKey] ?? 0;
                    const Icon = sliderIcons[settingKey];

                    return (
                        <div key={settingKey} className={s.slider_item}>
                            <div className={s.label_wrapper}>
                                {Icon && (
                                    <div className={s.icon_wrapper}>
                                        <Icon width={15} height={15} />
                                    </div>
                                )}
                                <label className={s.label}>{label}:</label>
                            </div>
                            <Slider
                                value={value}
                                min={min}
                                max={max}
                                step={step}
                                onChange={(newValue) =>
                                    filtersEnabled &&
                                    handleChangeVisualSettings(settingKey, newValue)
                                }
                                className={!filtersEnabled ? s.slider_disabled : ''}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AdjustVisualsBlock;
